import React, { ReactNode } from 'react';
import { buildClassName } from 'utils/build-class-name';
import IconButton from 'components/atomic/molecules/icon-button';
import styles from './style.module.scss';

//#region Types
export interface MultiSelectButtonGroupOption<T extends string | number> {
  value: T;
  label: string;
  /** Optional icon rendered to the left of the label */
  icon?: ReactNode;
  disabled?: boolean;
}

export type MultiSelectButtonGroupSize = 'small' | 'medium' | 'large';

export interface MultiSelectButtonGroupProps<T extends string | number> {
  /** Available options; any number can be selected at once */
  options: MultiSelectButtonGroupOption<T>[];
  /** Currently selected values */
  values: T[];
  /** Handler invoked with the next full set of selected values */
  onValuesChange: (values: T[]) => void;
  /** Size variant */
  size?: MultiSelectButtonGroupSize;
  /** Aria label describing the group for assistive technology */
  ariaLabel?: string;
  /** Disable the entire group */
  disabled?: boolean;
  /** Additional CSS class applied to the group */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}
//#endregion

/**
 * MultiSelectButtonGroup - A wrapping group of selectable chip/pill buttons.
 *
 * Atomic Level: Molecule
 *
 * A group of buttons where clicking a button marks it as selected. Any number
 * of buttons can be selected or unselected independently. Clicking an option
 * toggles its membership and calls `onValuesChange` with the next full array of
 * selected values.
 *
 * Features:
 * - Multi-select (any number of options active at once)
 * - Wrapping pill layout for variable-length option sets
 * - Optional per-option icon
 * - Generic over string or number values
 * - Multiple sizes: small, medium, large
 * - Per-option and whole-group disabling
 * - Accessible: `role="group"` with `aria-pressed` on each pill
 * - Composed from the IconButton molecule
 *
 * @example
 * ```tsx
 * <MultiSelectButtonGroup
 *   options={[
 *     { value: 'garage', label: 'Garage', icon: <GarageIcon /> },
 *     { value: 'pool', label: 'Pool', icon: <PoolIcon /> },
 *     { value: 'waterfront', label: 'Waterfront', icon: <WaterfrontIcon /> },
 *   ]}
 *   values={amenities}
 *   onValuesChange={setAmenities}
 *   ariaLabel="Amenities"
 * />
 * ```
 */
export default function MultiSelectButtonGroup<T extends string | number>({
  options,
  values,
  onValuesChange,
  size = 'medium',
  ariaLabel,
  disabled = false,
  className,
  testId,
}: MultiSelectButtonGroupProps<T>) {
  const toggleValue = (value: T) => {
    const next = values.includes(value)
      ? values.filter(current => current !== value)
      : [...values, value];
    onValuesChange(next);
  };

  return (
    <div
      className={buildClassName(className)}
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
    >
      {options.map(option => {
        const isSelected = values.includes(option.value);
        return (
          <IconButton
            key={String(option.value)}
            className={styles.button}
            label={option.label}
            leftIcon={option.icon}
            size={size}
            variant="tertiary"
            aria-pressed={isSelected}
            disabled={disabled || option.disabled}
            onClick={() => toggleValue(option.value)}
            testId={testId ? `${testId}-${String(option.value)}` : undefined}
          />
        );
      })}
    </div>
  );
}
